import React, { useState, useEffect } from 'react';
import { useSearchParams, Link } from 'react-router-dom';

const PRODUCTS = ['virtual-mentor', 'smartflow', 'translide', 'gressio'];

function Solutions({ translations }) {
  const [searchParams, setSearchParams] = useSearchParams();
  const productParam = searchParams.get('product');
  const normalizedProduct = productParam === 'transwork' ? 'gressio' : productParam;
  const initialTab = PRODUCTS.includes(normalizedProduct)
    ? normalizedProduct
    : 'virtual-mentor';
  const [activeTab, setActiveTab] = useState(initialTab);

  useEffect(() => {
    if (searchParams.get('product') === 'transwork') {
      setSearchParams({ product: 'gressio' }, { replace: true });
    }
  }, [searchParams, setSearchParams]);

  const switchTab = (tab) => {
    setActiveTab(tab);
    setSearchParams({ product: tab });
  };

  const tabs = [
    { id: 'virtual-mentor', label: translations.productVirtualMentor, logo: '/AI Virtual Mentor Logo.png' },
    { id: 'smartflow', label: translations.productSmartFlow, logo: '/AI-smartflow-Logo.png' },
    { id: 'translide', label: translations.productTranslide, logo: '/translide-logo.png' },
    { id: 'gressio', label: translations.productGressio, logo: '/gressio-logo.png' },
  ];

  return (
    <div className="solutions-page">
      <nav className="solutions-tabs">
        {tabs.map((tab) => (
          <button
            key={tab.id}
            className={`solutions-tab ${activeTab === tab.id ? 'active' : ''}`}
            onClick={() => switchTab(tab.id)}
          >
            <img src={tab.logo} alt="" className="solutions-tab-icon" />
            <span>{tab.label}</span>
          </button>
        ))}
      </nav>

      <div className="solutions-panel">
        {activeTab === 'virtual-mentor' && <VirtualMentor t={translations} />}
        {activeTab === 'smartflow' && <SmartFlow t={translations} />}
        {activeTab === 'translide' && <Translide t={translations} />}
        {activeTab === 'gressio' && <Gressio t={translations} />}
      </div>
    </div>
  );
}

function VirtualMentor({ t }) {
  return (
    <>
      <div className="sol-hero">
        <img src="/AI Virtual Mentor Logo.png" alt="AI Virtual Mentor" className="sol-hero-logo" />
        <h1>{t.vmTitle}</h1>
        <p>{t.vmSubtitle}</p>
      </div>
      <div className="sol-body">
        <section className="sol-card-row">
          <div className="sol-card-with-image">
            <div className="sol-card-text">
              <span className="sol-label">{t.vmJobsTitle}</span>
              <ul className="sol-list">
                <li>{t.vmJob1}</li>
                <li>{t.vmJob2}</li>
                <li>{t.vmJob3}</li>
                <li>{t.vmJob4}</li>
              </ul>
            </div>
            <div className="sol-card-img">
              <img src="/components/solutions/jobs-to-be-done.jpeg" alt={t.vmJobsTitle} />
            </div>
          </div>
        </section>
        <section className="sol-card-row reverse">
          <div className="sol-card-with-image">
            <div className="sol-card-img">
              <img src="/components/solutions/product-features.jpeg" alt={t.vmFeaturesTitle} />
            </div>
            <div className="sol-card-text">
              <span className="sol-label">{t.vmFeaturesTitle}</span>
              <ul className="sol-list">
                <li>{t.vmFeature1}</li>
                <li>{t.vmFeature2}</li>
                <li>{t.vmFeature3}</li>
                <li>{t.vmFeature4}</li>
              </ul>
            </div>
          </div>
        </section>
        <section className="sol-card-row">
          <div className="sol-card-with-image">
            <div className="sol-card-text">
              <span className="sol-label">{t.vmUsageTitle}</span>
              <p className="sol-desc">{t.vmUsageDesc}</p>
              <ul className="sol-list">
                <li>{t.vmUsage1}</li>
                <li>{t.vmUsage2}</li>
                <li>{t.vmUsage3}</li>
                <li>{t.vmUsage4}</li>
              </ul>
            </div>
            <div className="sol-card-img">
              <img src="/components/solutions/usage-scenario.jpeg" alt={t.vmUsageTitle} />
            </div>
          </div>
        </section>
        <section className="sol-card-row reverse">
          <div className="sol-card-with-image">
            <div className="sol-card-img">
              <img src="/components/solutions/one-stop-knowledge-base.jpeg" alt={t.vmKBTitle} />
            </div>
            <div className="sol-card-text">
              <span className="sol-label">{t.vmKBTitle}</span>
              <p className="sol-desc">{t.vmKBDesc}</p>
              <ul className="sol-list">
                <li>{t.vmKB1}</li>
                <li>{t.vmKB2}</li>
                <li>{t.vmKB3}</li>
                <li>{t.vmKB4}</li>
              </ul>
            </div>
          </div>
        </section>
        <div className="tl-cta-row">
          <Link to="/contact" className="btn btn-primary">{t.contactSales} &rarr;</Link>
        </div>
      </div>
    </>
  );
}

function SmartFlow({ t }) {
  const capabilities = [
    { title: t.sfCap1Title, desc: t.sfCap1Desc, icon: '\u2699\uFE0F' },
    { title: t.sfCap2Title, desc: t.sfCap2Desc, icon: '\uD83D\uDCDA' },
    { title: t.sfCap3Title, desc: t.sfCap3Desc, icon: '\uD83D\uDCC4' },
    { title: t.sfCap4Title, desc: t.sfCap4Desc, icon: '\u2705' },
    { title: t.sfCap5Title, desc: t.sfCap5Desc, icon: '\uD83D\uDCCA' },
    { title: t.sfCap6Title, desc: t.sfCap6Desc, icon: '\uD83D\uDD0D' },
  ];
  const steps = [
    { num: '01', title: t.sfStep1Title, desc: t.sfStep1Desc },
    { num: '02', title: t.sfStep2Title, desc: t.sfStep2Desc },
    { num: '03', title: t.sfStep3Title, desc: t.sfStep3Desc },
    { num: '04', title: t.sfStep4Title, desc: t.sfStep4Desc },
  ];
  const useCases = [
    { title: t.sfUseCase1Title, desc: t.sfUseCase1Desc },
    { title: t.sfUseCase2Title, desc: t.sfUseCase2Desc },
    { title: t.sfUseCase3Title, desc: t.sfUseCase3Desc },
  ];

  return (
    <>
      <div className="sol-hero">
        <img src="/AI-smartflow-Logo.png" alt="AI SmartFlow" className="sol-hero-logo" />
        <h1>{t.sfTitle}</h1>
        <p>{t.sfSubtitle}</p>
      </div>
      <div className="sol-body">
        <section className="sf-capabilities">
          <h2 className="sf-section-title">{t.sfCapabilities}</h2>
          <div className="sf-cap-grid">
            {capabilities.map((cap, i) => (
              <div key={i} className="sf-cap-card">
                <span className="sf-cap-icon">{cap.icon}</span>
                <h3>{cap.title}</h3>
                <p>{cap.desc}</p>
              </div>
            ))}
          </div>
        </section>

        <section className="sf-workflow">
          <h2 className="sf-section-title">{t.sfHowTitle}</h2>
          <div className="sf-steps">
            {steps.map((step, i) => (
              <div key={i} className="sf-step">
                <div className="sf-step-num">{step.num}</div>
                <h3>{step.title}</h3>
                <p>{step.desc}</p>
                {i < steps.length - 1 && <div className="sf-step-connector" />}
              </div>
            ))}
          </div>
        </section>

        <section className="sf-usecases">
          <h2 className="sf-section-title">{t.sfUseCases}</h2>
          <div className="sf-usecase-grid">
            {useCases.map((uc, i) => (
              <div key={i} className="sf-usecase-card">
                <h3>{uc.title}</h3>
                <p>{uc.desc}</p>
              </div>
            ))}
          </div>
        </section>
        <div className="tl-cta-row">
          <Link to="/contact" className="btn btn-primary">{t.contactSales} &rarr;</Link>
        </div>
      </div>
    </>
  );
}

function Translide({ t }) {
  const features = [
    { title: t.tlFeature1Title, desc: t.tlFeature1Desc },
    { title: t.tlFeature2Title, desc: t.tlFeature2Desc },
    { title: t.tlFeature3Title, desc: t.tlFeature3Desc },
  ];
  return (
    <>
      <div className="sol-hero">
        <img src="/translide-logo.png" alt="Translide" className="sol-hero-logo" />
        <h1>{t.tlTitle}</h1>
        <p>{t.tlSubtitle}</p>
      </div>
      <div className="sol-body">
        <div className="tl-features">
          {features.map((f, i) => (
            <div key={i} className="tl-feature-card">
              <h3>{f.title}</h3>
              <p>{f.desc}</p>
            </div>
          ))}
        </div>
        <div className="tl-cta-row">
          <a href="https://translide.co" target="_blank" rel="noopener noreferrer" className="btn btn-primary">{t.tlCTA} &rarr;</a>
        </div>
      </div>
    </>
  );
}

function Gressio({ t }) {
  const features = [
    { title: t.grFeature1Title, desc: t.grFeature1Desc },
    { title: t.grFeature2Title, desc: t.grFeature2Desc },
    { title: t.grFeature3Title, desc: t.grFeature3Desc },
  ];
  return (
    <>
      <div className="sol-hero">
        <img src="/gressio-logo.png" alt="Gressio" className="sol-hero-logo" />
        <h1>{t.grTitle}</h1>
        <p>{t.grSubtitle}</p>
      </div>
      <div className="sol-body">
        <div className="tl-features">
          {features.map((f, i) => (
            <div key={i} className="tl-feature-card">
              <h3>{f.title}</h3>
              <p>{f.desc}</p>
            </div>
          ))}
        </div>
        <div className="tl-cta-row">
          <a href="https://gressio.ai" target="_blank" rel="noopener noreferrer" className="btn btn-primary">{t.grCTA} &rarr;</a>
        </div>
      </div>
    </>
  );
}

export default Solutions;
